import { useEffect, useRef, useState, useCallback } from "react";

import { DemoItem, generateBatch, getPublicLogo } from "common/utils/helper";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// Type definitions for API response
interface PaginationMetadata {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface DailyMatchupsResponse {
  matchups: any[];
  pagination: PaginationMetadata;
}

// Utility function to extract Voteid from URL
const getVoteIdFromUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('id') || '';
};

// Utility function to remove id from URL
const removeIdFromUrl = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('id')) {
    urlParams.delete('id');
    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }
};

export const useDailyMatchups = (userInfo: any, initialBatchSize = 10, functionName: string = "getDailyMatchupsByUser") => {
  const [items, setItems] = useState<DemoItem[]>([]);
  const [dataSource, setDataSource] = useState<"demo" | "user">("demo");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const getMatchupsFunction = httpsCallable(functions, functionName);

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) {
      return;
    }
    setIsLoading(true);

    try {
      // ✅ Logged-in: fetch next page
      const pageSize = 20;
      const nextPage = page + 1;
      const voteId = getVoteIdFromUrl();
      const query = { pageSize, page: nextPage, userId: userInfo?.uid, voteId };
      const res = await getMatchupsFunction(query);
      const responseData = (res?.data as DailyMatchupsResponse) || { matchups: [], pagination: {} as PaginationMetadata };
      const newData = responseData?.matchups ?? [];
      const pagination = responseData?.pagination;

      // Check pagination metadata to determine if we should continue
      const currentPage = pagination?.currentPage ?? nextPage;
      const totalPages = pagination?.totalPages ?? 1;
      const hasNextPage = pagination?.hasNextPage ?? false;

      // If we've reached the last page, stop loading more
      if (currentPage >= totalPages || !hasNextPage || newData.length === 0) {
        setHasMore(false);
        console.log('🛑 Reached last page:', currentPage, '/', totalPages);
        if (newData.length === 0) {
          return;
        }
      }

      const mapped: DemoItem[] = newData.map((d: any, idx: number) => {
        const title = d.headline || d.title || `Matchup #${idx + 1}`;
        const possibleVideo = d.videoUrl || d.video;
        const possibleImage = d.imageUrl || d.image || getPublicLogo("BTC");
        const leftVotes = d.leftVotes ?? d.option1Votes ?? d.upVotes ?? 50;
        const rightVotes = d.rightVotes ?? d.option2Votes ?? d.downVotes ?? 50;
        const leftLabel = d.leftLabel ?? d.option1 ?? d.option1Text ?? "Option 1";
        const rightLabel = d.rightLabel ?? d.option2 ?? d.option2Text ?? "Option 2";
        const vote = (d.vote as any) ?? { up: 0, down: 0, total: 0 };
        const voted = d.voted ?? false;
        const isLiked = d.isLiked ?? false;
        const votedOption = d.votedOption ?? null;

        return {
          id: String(d.id ?? `${page}-${idx}`),
          title,
          mediaSrc: possibleVideo || possibleImage,
          mediaType: possibleVideo ? "video" : "image",
          vote,
          leftVotes,
          rightVotes,
          leftLabel,
          rightLabel,
          thumbs_meaning: d.thumbs_meaning,
          userId: d.userId ?? d.user_id,
          voted,
          isLiked,
          votedOption: d?.votedOption,
          product_image: d?.product_image || "",
          product_name: d?.product_name || "",
          product_link: d?.product_link || "",
          endDate: d?.endDate || "",
        } as DemoItem;
      });

      setItems((prev) => [...prev, ...mapped]);
      setPage(nextPage);
      setDataSource("user");
      console.log('✨ Added', mapped.length, 'items, total now:', items.length + mapped.length, '| Page:', currentPage, '/', totalPages);
    } catch (err) {
      console.error("Error loading more matchups:", err);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasMore, page, userInfo?.uid, getMatchupsFunction]);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const id = getVoteIdFromUrl();
        const query = { pageSize: 20, page: 1, userId: userInfo?.uid || "", id };
        const res = await getMatchupsFunction(query);
        const responseData = (res?.data as DailyMatchupsResponse) || { matchups: [], pagination: {} as PaginationMetadata };
        const matchups = responseData?.matchups ?? [];
        const pagination = responseData?.pagination;

        // Check if no data was found
        if (matchups.length === 0) {
          setIsEmpty(true);
          setHasMore(false);
          setItems([]);
          setDataSource("user");
          return;
        }

        // Check pagination to set hasMore correctly from the start
        const currentPage = pagination?.currentPage ?? 1;
        const totalPages = pagination?.totalPages ?? 1;
        const hasNextPage = pagination?.hasNextPage ?? false;

        // If we're already on the last page, don't allow more loading
        if (currentPage >= totalPages || !hasNextPage) {
          setHasMore(false);
          console.log('🛑 Initial load: Already on last page', currentPage, '/', totalPages);
        }

        setIsEmpty(false);
        const mapped: DemoItem[] = matchups.map((d: any, idx: number) => ({
          id: String(d.id ?? idx),
          title: d.headline || `Matchup #${idx + 1}`,
          mediaSrc: d.videoUrl || d.imageUrl || getPublicLogo("BTC"),
          mediaType: (d.videoUrl ? "video" : "image") as "video" | "image",
          vote: d.votes ?? "none",
          leftVotes: d.leftVotes ?? 50,
          rightVotes: d.rightVotes ?? 50,
          leftLabel: d.leftLabel ?? "Option 1",
          rightLabel: d.rightLabel ?? "Option 2",
          thumbs_meaning: d.thumbs_meaning,
          userId: d.userId ?? d.user_id,
          voted: d.voted ?? false,
          isLiked: d.isLiked ?? false,
          votedOption: d?.votedOption,
          product_image: d?.product_image || "",
          product_name: d?.product_name || "",
          product_link: d?.product_link || "",
          endDate: d?.endDate || "",
        } as DemoItem));

        // Remove id from URL after 3 seconds of successful API response
        if (id) {
          setTimeout(() => {
            removeIdFromUrl();
          }, 4000);
        }

        window.scrollTo(0, 10);
        setItems(mapped);
        setDataSource("user");
      } catch (error) {
        console.error("Error fetching initial matchups:", error);
        setIsEmpty(true);
        setHasMore(false);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitial();
  }, [userInfo?.uid, initialBatchSize]);

  return { items, setItems, dataSource, loadMore, isLoading, hasMore, setIsLoading, isEmpty, initialLoading };
};
